// SE-2251: generate the bulk-invite CSV template on the fly so its columns match
// the viewer. Base columns are role/training-stage driven (mirroring the former
// static S3 templates). SE-3118: identifier columns come from the network
// identifier config — one per identifier, named by identifierColumn (npi,
// simplLearnId, aamcId, …), with example values guessed from each identifier's
// regex. Headers are the field names the CSV parser expects.

import type {
  IdentifierType,
  ProgramIdentifierConfig
} from "../Networks/networksAPI";
import {
  exampleIdentifierValue,
  identifierColumn
} from "../Networks/identifierUtils";

export type BulkTemplateContext =
  | "sysadmin"
  | "fellowship"
  | "medicalschool"
  | "residency"
  | "programadmin";

// Base columns per context — matches the historical BulkInvite_*.csv files minus
// the identifier columns, which are inserted after "email" in config order.
const CONTEXT_COLUMNS: Record<BulkTemplateContext, string[]> = {
  sysadmin: [
    "firstName", "lastName", "email", "pgy", "yearInStage",
    "role", "programId", "traineeType", "jobTitle"
  ],
  fellowship: [
    "firstName", "lastName", "email", "pgy", "yearInStage",
    "role", "traineeType", "jobTitle"
  ],
  medicalschool: [
    "firstName", "lastName", "email", "yearInStage",
    "role", "traineeType", "jobTitle"
  ],
  residency: [
    "firstName", "lastName", "email", "pgy",
    "role", "traineeType", "jobTitle"
  ],
  programadmin: [
    "firstName", "lastName", "email", "pgy",
    "role", "traineeType", "jobTitle"
  ]
};

const TRAINEE_TYPE_EXAMPLE: Record<BulkTemplateContext, string> = {
  sysadmin: "Categorical",
  fellowship: "Fellow",
  medicalschool: "MedicalStudent",
  residency: "Categorical",
  programadmin: "Categorical"
};

// One identifier column: a row's example cell gets a value only where that row's
// role requires the identifier.
export type TemplateIdentifier = {
  key: string;
  displayName: string;
  validationRegex: string | null;
  requiredByRole: Record<string, boolean>;
};

/**
 * SysAdmin has no program context: a column for every identifier type in the
 * system, with an example on both rows.
 */
export function sysAdminTemplateIdentifiers(
  types: IdentifierType[]
): TemplateIdentifier[] {
  return types.map((t) => ({
    ...t,
    requiredByRole: { Trainee: true, Attending: true }
  }));
}

/**
 * Merge the viewer's program configs into one column per identifier, ordered by
 * `types` (date added). Required for a role if ANY program requires it (same
 * rule the resolver uses across a program's networks).
 */
export function templateIdentifiersFromConfigs(
  configs: Record<string, ProgramIdentifierConfig>,
  types: IdentifierType[]
): TemplateIdentifier[] {
  const byKey = new Map<string, TemplateIdentifier>();
  for (const cfg of Object.values(configs)) {
    for (const [role, items] of Object.entries(cfg.identifiersByRole)) {
      for (const it of items) {
        let entry = byKey.get(it.key);
        if (!entry) {
          entry = {
            key: it.key,
            displayName: it.displayName,
            validationRegex: it.validationRegex,
            requiredByRole: {}
          };
          byKey.set(it.key, entry);
        }
        entry.requiredByRole[role] = entry.requiredByRole[role] || it.required;
      }
    }
  }
  const rank = new Map(types.map((t, i) => [t.key, i]));
  const at = (key: string) => rank.get(key) ?? types.length;
  return Array.from(byKey.values()).sort((a, b) => at(a.key) - at(b.key));
}

// The two example rows (Trainee + Attending), keyed by column, matching the
// former static templates. Identifier cells are filled per role below.
function exampleValues(ctx: BulkTemplateContext) {
  const hasYear =
    ctx === "sysadmin" || ctx === "fellowship" || ctx === "medicalschool";
  const traineePgy =
    ctx === "sysadmin" || ctx === "fellowship"
      ? "5"
      : ctx === "medicalschool"
      ? ""
      : "1";
  const trainee: Record<string, string> = {
    firstName: "User",
    lastName: "Trainee",
    email: "traineeuser@gmail.com",
    pgy: traineePgy,
    yearInStage: hasYear ? "1" : "",
    role: "Trainee",
    programId: "",
    traineeType: TRAINEE_TYPE_EXAMPLE[ctx],
    jobTitle: "Trainee"
  };
  const attending: Record<string, string> = {
    firstName: "User",
    lastName: "Attending",
    email: "attendinguser@gmail.com",
    pgy: "",
    yearInStage: "",
    role: "Attending",
    programId: "",
    traineeType: "",
    jobTitle: "Attending"
  };
  return [trainee, attending];
}

// Rows are joined with \r\n, so a cell containing \r (or \n or ") must be quoted
// too — otherwise a stray carriage return would break the CSV structure.
const escapeCell = (v: string) =>
  /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

/** Build the bulk-invite CSV template text for the given context and identifiers. */
export function buildBulkInviteTemplateCsv(
  ctx: BulkTemplateContext,
  identifiers: TemplateIdentifier[]
): string {
  const base = CONTEXT_COLUMNS[ctx];
  const afterEmail = base.indexOf("email") + 1;
  const columns = [
    ...base.slice(0, afterEmail),
    ...identifiers.map((it) => identifierColumn(it.key)),
    ...base.slice(afterEmail)
  ];
  const examples = exampleValues(ctx);
  examples.forEach((row, variant) => {
    for (const it of identifiers) {
      if (it.requiredByRole[row.role]) {
        row[identifierColumn(it.key)] = exampleIdentifierValue(it, variant);
      }
    }
  });
  const rows = [columns, ...examples.map((r) => columns.map((c) => r[c] ?? ""))];
  return rows.map((r) => r.map(escapeCell).join(",")).join("\r\n") + "\r\n";
}

// Non-identifier columns the bulk upload understands (every template's columns
// minus the identifier ones).
const BASE_COLUMNS = new Set(Object.values(CONTEXT_COLUMNS).flat());

/**
 * Split an uploaded CSV's columns: base and identifier columns are kept;
 * anything else (e.g. a "notes" column, or a misspelled identifier header) is
 * dropped from the rows and reported so the page can warn about it.
 */
export function keepKnownBulkColumns<Row extends Record<string, unknown>>(
  rows: Row[],
  types: IdentifierType[]
): { rows: Row[]; ignoredColumns: string[] } {
  const known = new Set([
    ...Array.from(BASE_COLUMNS),
    ...types.map((t) => identifierColumn(t.key))
  ]);
  const ignored = new Set<string>();
  const kept = rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (const [column, value] of Object.entries(row)) {
      if (known.has(column)) out[column] = value;
      // Papa puts cells beyond the header row under __parsed_extra; drop quietly.
      else if (column !== "__parsed_extra") ignored.add(column);
    }
    return out as Row;
  });
  return { rows: kept, ignoredColumns: Array.from(ignored) };
}

/** Trigger a browser download of the generated CSV. */
export function downloadBulkInviteTemplate(
  ctx: BulkTemplateContext,
  identifiers: TemplateIdentifier[]
): void {
  const csv = buildBulkInviteTemplateCsv(ctx, identifiers);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "BulkInvite_Template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
